import { PeerOptions } from '../../../hooks/usePeers';

export type ContentReviewType =
  | 'display_name'
  | 'about_me'
  | 'profile_photo'
  | 'background_tags'
  | 'experience_audio'
  | 'experience_video';

const ORDERED_TYPES: ContentReviewType[] = [
  'profile_photo',
  'background_tags',
  'display_name',
  'about_me',
  'experience_audio',
  'experience_video',
];

export const deriveContentReviewTypes = (filters: PeerOptions): ContentReviewType[] => {
  const types = new Set<ContentReviewType>();

  if (filters.displayNameStatus?.toLowerCase?.() === 'pending') types.add('display_name');
  if (filters.aboutMeStatus?.toLowerCase?.() === 'pending') types.add('about_me');
  if (filters.profilePhotoApproved === false) types.add('profile_photo');
  if (filters.backgroundTagApproved === false) types.add('background_tags');

  if (filters.needsExperienceApproval === true) {
    const mts = filters.needsExperienceApprovalMediaTypes || [];
    if (mts.includes('audio')) types.add('experience_audio');
    if (mts.includes('video')) types.add('experience_video');
    if (mts.length === 0) {
      types.add('experience_audio');
      types.add('experience_video');
    }
  }

  return ORDERED_TYPES.filter((t) => types.has(t));
};

export const isAllContentReviewTypesSelected = (filters: PeerOptions): boolean => {
  const types = deriveContentReviewTypes(filters);
  return ORDERED_TYPES.every((t) => types.includes(t));
};

export const applyContentReviewTypesToFilters = (base: PeerOptions, types: string[] | Set<string>): PeerOptions => {
  const set = types instanceof Set ? types : new Set(types.map((t) => t.toLowerCase()));
  const next: PeerOptions = { ...base };

  if (set.has('profile_photo')) next.profilePhotoApproved = false;
  if (set.has('display_name')) next.displayNameStatus = 'pending';
  if (set.has('about_me')) next.aboutMeStatus = 'pending';
  if (set.has('background_tags')) next.backgroundTagApproved = false;

  const mts: Array<'audio' | 'video'> = [];
  if (set.has('experience_audio')) mts.push('audio');
  if (set.has('experience_video')) mts.push('video');
  if (mts.length > 0) {
    next.needsExperienceApproval = true;
    next.needsExperienceApprovalMediaTypes = mts;
  }

  next.needsAnyContentReview = isAllContentReviewTypesSelected(next) ? true : undefined;
  return next;
};
